import Spinner from './ui/spinner';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import useDebounce from '../hooks/useDebounce';
import useFeedbackSocket from '../hooks/useFeedbackSocket';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

import {
  Search,
  Clock,
  CircleAlert,
  CircleCheckBig,
  MessageCircle,
  ArrowLeft,
  Mail,
  Upload,
  Calendar,
  Image as ImageIcon,
  Send,
  X,
  User,
} from 'lucide-react';
import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { getImageUrl } from '../utils/imageUrl';
import ImagePreviewOverlay from './ImagePreviewOverlay';
import {
  IUserFeedback,
  IFeedbackStats,
  IFeedbackReply,
  IPaginated,
} from '../types';
import { formatDateTime } from '../utils/formatDate';
import Pagination from './Pagination';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';

interface IFeedbackManagementProps {
  initialFeedbackId?: string;
  onNavigateDetail: (id: string) => void;
  onBackToList: () => void;
}

const FeedbackManagement = ({
  initialFeedbackId,
  onNavigateDetail,
  onBackToList,
}: IFeedbackManagementProps) => {
  const { t } = useTranslation();

  const STATUS_LABELS: Record<
    string,
    {
      label: string;
      variant: 'secondary' | 'default' | 'success' | 'outline';
      icon: typeof Clock;
    }
  > = {
    pending: {
      label: t('feedback.pending'),
      variant: 'secondary',
      icon: Clock,
    },
    processing: {
      label: t('feedback.processing'),
      variant: 'default',
      icon: CircleAlert,
    },
    resolved: {
      label: t('feedback.resolved'),
      variant: 'success',
      icon: CircleCheckBig,
    },
  };

  const TYPE_LABELS: Record<string, { label: string }> = {
    feature_request: {
      label: t('feedback.type_feature_request'),
    },
    bug_report: {
      label: t('feedback.type_bug_report'),
    },
    suggestion: {
      label: t('feedback.type_suggestion'),
    },
    other: {
      label: t('feedback.type_other'),
    },
  };

  const [feedbacks, setFeedbacks] = useState<IUserFeedback[]>([]);
  const [stats, setStats] = useState<IFeedbackStats | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('filter') || 'all';
  const typeFilter = searchParams.get('type') || 'all';
  const search = searchParams.get('search') || '';
  const page = Number(searchParams.get('page')) || 1;

  const setStatusFilter = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== 'all') {
          next.set('filter', value);
        } else {
          next.delete('filter');
        }

        next.delete('page');

        return next;
      });
    },
    [setSearchParams],
  );

  const setTypeFilter = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== 'all') {
          next.set('type', value);
        } else {
          next.delete('type');
        }

        next.delete('page');

        return next;
      });
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value) {
          next.set('search', value);
        } else {
          next.delete('search');
        }

        next.delete('page');

        return next;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (newPage > 1) {
          next.set('page', String(newPage));
        } else {
          next.delete('page');
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<IUserFeedback | null>(null);
  const [detailStatus, setDetailStatus] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<
    { key: string; url: string }[]
  >([]);
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_LIMIT = 20;

  // Real-time feedback replies via WebSocket
  useFeedbackSocket(selected?.id || null, (reply) => {
    if (selected) {
      // Avoid duplicates (e.g. admin's own reply already added via HTTP response)
      const alreadyExists = (selected.replies || []).some(
        (r) => r.id === reply.id,
      );

      if (!alreadyExists) {
        setSelected({
          ...selected,
          replies: [...(selected.replies || []), reply],
        });
      }
    }
  });

  useEffect(() => {
    if (initialFeedbackId) {
      handleLoadDetail(initialFeedbackId);
    }
    loadData();
  }, []);

  useEffect(() => {
    loadData(1);
  }, [statusFilter, typeFilter]);

  // Debounced search on type
  useDebounce(
    () => {
      loadData(1);
    },
    300,
    [search],
  );

  const loadData = async (p = page) => {
    try {
      const params = new URLSearchParams();

      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('type', typeFilter);
      if (search) params.append('search', search);
      params.append('page', String(p));
      params.append('limit', String(PAGE_LIMIT));

      const qs = params.toString();
      const [res, s] = await Promise.all([
        api.get<IPaginated<IUserFeedback>>(`/backoffice/feedback?${qs}`),
        api.get<IFeedbackStats>('/backoffice/feedback/stats'),
      ]);
      setFeedbacks(res.data || []);
      setTotal(res.total);
      setTotalPages(res.totalPages);
      setStats(s);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDetail = async (id: string) => {
    try {
      const detail = await api.get<IUserFeedback>(`/backoffice/feedback/${id}`);
      setSelected(detail);
      setDetailStatus(detail.status);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRowClick = (feedback: IUserFeedback) => {
    onNavigateDetail(feedback.id);
    handleLoadDetail(feedback.id);
  };

  const handleBack = () => {
    setSelected(null);
    onBackToList();
    loadData();
  };

  const handleSaveStatus = async () => {
    if (!selected) return;

    try {
      await api.patch(`/backoffice/feedback/${selected.id}/status`, {
        status: detailStatus,
      });
      setSelected({ ...selected, status: detailStatus });
      loadData();
      toast.success(t('common.save_success'));
    } catch (err: any) {
      toast.error(err.message || t('common.operation_failed'));
    }
  };

  const handleSendReply = async () => {
    if (!selected || (!replyContent.trim() && replyAttachments.length === 0))
      return;

    setSubmitting(true);

    try {
      const reply = await api.post<IFeedbackReply>(
        `/backoffice/feedback/${selected.id}/reply`,
        {
          content: replyContent.trim(),
          isInternal,
          attachments: replyAttachments.map((a) => a.key),
        },
      );
      setSelected({
        ...selected,
        replies: [...(selected.replies || []), reply],
      });
      setReplyContent('');
      setReplyAttachments([]);
      setIsInternal(false);
    } catch (err: any) {
      toast.error(err.message || t('common.operation_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setUploading(true);

    try {
      const isImage = file.type.startsWith('image/');

      if (isImage) {
        const res = await api.upload<{ imageKey: string; imageUrl: string }>(
          '/backoffice/upload-image',
          file,
          { private: 'true' },
        );
        setReplyAttachments((prev) => [
          ...prev,
          { key: res.imageKey, url: res.imageUrl },
        ]);
      } else {
        const res = await api.upload<{
          attachmentKey: string;
          attachmentUrl: string;
        }>('/backoffice/upload-attachment', file);
        setReplyAttachments((prev) => [
          ...prev,
          { key: res.attachmentKey, url: res.attachmentUrl },
        ]);
      }
    } catch (err: any) {
      toast.error(err.message || t('common.operation_failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) return <Spinner />;

  // ==================== Detail View ====================
  if (selected) {
    const st = STATUS_LABELS[selected.status] || STATUS_LABELS.pending;
    const tp = TYPE_LABELS[selected.type] || TYPE_LABELS.other;

    return (
      <div className="p-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={handleBack}
            className="mb-4 -ml-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('feedback.back_to_list')}
          </button>

          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                {selected.title || selected.content.slice(0, 60)}
              </h1>
              <Badge variant={st.variant} className="gap-1">
                <st.icon className="w-3 h-3" />
                {st.label}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDateTime(selected.createdAt)}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Feedback Content */}
            <div className="bg-white rounded-lg border border-border p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="shrink-0">
                  {selected.user?.avatar ? (
                    <img
                      src={getImageUrl(selected.user.avatar)!}
                      alt=""
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                      {(
                        selected.user?.displayName ||
                        selected.user?.username ||
                        '?'
                      ).charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {selected.user?.displayName ||
                        selected.user?.username ||
                        '—'}
                    </span>
                    <Badge variant="outline">{tp.label}</Badge>
                  </div>
                  <div className="text-sm text-gray-500 space-y-0.5">
                    {selected.email && (
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {selected.email}
                      </div>
                    )}
                    {(selected.deviceInfo || selected.osVersion) && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {[selected.deviceInfo, selected.osVersion]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                    )}
                    {selected.appVersion && (
                      <div className="text-xs">
                        {t('feedback.version')}: {selected.appVersion}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="prose max-w-none">
                <p className="text-gray-700 whitespace-pre-wrap">
                  {selected.content}
                </p>
              </div>

              {/* Screenshots */}
              {selected.attachments && selected.attachments.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4" />
                    {t('feedback.screenshots')} ({selected.attachments.length})
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    {selected.attachments.map((url, i) => (
                      <img
                        key={i}
                        src={getImageUrl(url) || url}
                        alt=""
                        className="rounded-lg border border-border w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setPreviewImage(getImageUrl(url) || url)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Responses */}
            <div className="bg-white rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('feedback.reply_count').replace(
                  '{count}',
                  String(selected.replies?.length || 0),
                )}
              </h2>

              <div className="space-y-4 mb-6">
                {!selected.replies || selected.replies.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">
                    {t('feedback.no_replies')}
                  </p>
                ) : (
                  selected.replies.map((reply) => (
                    <div
                      key={reply.id}
                      className={`p-4 rounded-lg border-l-4 ${
                        reply.isInternal
                          ? 'bg-amber-50 border-l-amber-500 border-t border-r border-b border-amber-200'
                          : reply.authorRole === 'admin'
                            ? 'bg-blue-50 border-l-blue-500 border-t border-r border-b border-blue-200'
                            : 'bg-gray-50 border-l-gray-400 border-t border-r border-b border-border'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0 overflow-hidden ${
                            reply.isInternal
                              ? 'bg-amber-500'
                              : reply.authorRole === 'admin'
                                ? 'bg-blue-500'
                                : 'bg-gray-500'
                          }`}
                        >
                          {reply.authorAvatar ? (
                            <img
                              src={
                                getImageUrl(reply.authorAvatar) ||
                                reply.authorAvatar
                              }
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            reply.authorName.charAt(0)
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900">
                              {reply.authorName}
                            </span>
                            {reply.authorRole === 'admin' &&
                              !reply.isInternal && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-white border border-blue-200 text-blue-700">
                                  {t('feedback.admin_badge')}
                                </span>
                              )}
                            {reply.isInternal && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-300">
                                🔒 {t('feedback.internal_note')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(reply.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="pl-10">
                        <p
                          className={`text-sm whitespace-pre-wrap ${
                            reply.isInternal
                              ? 'text-amber-900'
                              : 'text-gray-700'
                          }`}
                        >
                          {reply.content}
                        </p>
                        {reply.attachments && reply.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <div className="grid grid-cols-3 gap-2">
                              {reply.attachments
                                .filter((url) =>
                                  /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                    url.split('?')[0],
                                  ),
                                )
                                .map((url, i) => {
                                  const resolvedUrl = getImageUrl(url) || url;
                                  return (
                                    <img
                                      key={i}
                                      src={resolvedUrl}
                                      alt=""
                                      className="rounded-lg border border-border w-full h-32 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() =>
                                        setPreviewImage(resolvedUrl)
                                      }
                                    />
                                  );
                                })}
                            </div>
                            {reply.attachments
                              .filter(
                                (url) =>
                                  !/\.(jpg|jpeg|png|gif|webp)$/i.test(
                                    url.split('?')[0],
                                  ),
                              )
                              .map((url, i) => {
                                const resolvedUrl = getImageUrl(url) || url;
                                const fileName =
                                  url.split('/').pop()?.split('?')[0] || 'File';
                                const ext =
                                  fileName.split('.').pop()?.toUpperCase() ||
                                  'FILE';
                                return (
                                  <a
                                    key={i}
                                    href={resolvedUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-2 rounded-lg border border-border bg-red-50 hover:bg-red-100 transition-colors w-fit"
                                  >
                                    <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                                      {ext}
                                    </div>
                                    <span className="text-xs text-gray-700 truncate max-w-[200px]">
                                      {fileName}
                                    </span>
                                  </a>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Form */}
              <div className="border-t border-border pt-4">
                <div className="mb-3">
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder={t('feedback.reply_placeholder')}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Upload Preview */}
                {replyAttachments.length > 0 && (
                  <div className="mb-3 grid grid-cols-4 gap-2">
                    {replyAttachments.map((att, i) => {
                      const resolvedUrl = getImageUrl(att.url) || att.url;
                      const urlPath = att.url.split('?')[0];
                      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
                        urlPath,
                      );
                      const fileName = urlPath.split('/').pop() || 'File';
                      const ext =
                        fileName.split('.').pop()?.toUpperCase() || 'FILE';

                      return (
                        <div key={i} className="relative group">
                          {isImage ? (
                            <img
                              src={resolvedUrl}
                              alt=""
                              className="w-full h-24 object-cover rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => setPreviewImage(resolvedUrl)}
                            />
                          ) : (
                            <div className="w-full h-24 rounded-lg border border-border bg-gray-50 flex flex-col items-center justify-center gap-1">
                              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-[10px] font-bold">
                                {ext}
                              </div>
                              <span className="text-[10px] text-gray-500 truncate max-w-full px-1">
                                {fileName}
                              </span>
                            </div>
                          )}
                          <button
                            onClick={() =>
                              setReplyAttachments((prev) =>
                                prev.filter((_, idx) => idx !== i),
                              )
                            }
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      {t('feedback.internal_note')}
                    </label>

                    <label className="cursor-pointer">
                      <input
                        type="file"
                        onChange={handleUploadFile}
                        className="hidden"
                        disabled={uploading}
                      />
                      <div className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-md hover:bg-gray-100 transition-colors">
                        <Upload className="w-4 h-4" />
                        <span>
                          {uploading
                            ? t('common.uploading')
                            : t('feedback.upload')}
                        </span>
                      </div>
                    </label>
                  </div>

                  <button
                    onClick={handleSendReply}
                    disabled={
                      (!replyContent.trim() && replyAttachments.length === 0) ||
                      submitting
                    }
                    className="px-4 py-2 bg-[#030213] text-white rounded-lg text-sm font-medium hover:bg-[#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {t('feedback.send_reply')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Management */}
            <div className="bg-white rounded-lg border border-border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {t('feedback.management')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('feedback.status')}
                  </label>
                  <Select
                    value={detailStatus}
                    onValueChange={(val) => setDetailStatus(val)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        {t('feedback.pending')}
                      </SelectItem>
                      <SelectItem value="processing">
                        {t('feedback.processing')}
                      </SelectItem>
                      <SelectItem value="resolved">
                        {t('feedback.resolved')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <button
                  onClick={handleSaveStatus}
                  disabled={detailStatus === selected.status}
                  className="w-full px-4 py-2 bg-[#030213] text-white rounded-lg text-sm font-medium hover:bg-[#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('feedback.save_changes')}
                </button>
              </div>
            </div>

            {/* Feedback Info */}
            <div className="bg-white rounded-lg border border-border p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {t('feedback.info')}
              </h3>

              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500 mb-1">
                    {t('feedback.feedback_id')}
                  </p>
                  <p className="font-mono text-xs bg-gray-50 px-2 py-1 rounded break-all">
                    {selected.id}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 mb-1">
                    {t('feedback.submitted_time')}
                  </p>
                  <p className="text-gray-900">
                    {formatDateTime(selected.createdAt)}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 mb-1">
                    {t('feedback.last_updated')}
                  </p>
                  <p className="text-gray-900">
                    {formatDateTime(selected.updatedAt)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {previewImage && (
          <ImagePreviewOverlay
            src={previewImage}
            alt="Preview"
            onClose={() => setPreviewImage(null)}
          />
        )}
      </div>
    );
  }

  // ==================== List View ====================
  return (
    <div className="p-4 lg:p-8 flex flex-col gap-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {t('feedback.title')}
        </h1>
        <p className="text-base text-gray-600 mt-2">{t('feedback.subtitle')}</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: t('feedback.pending'),
              value: stats.pending,
              Icon: Clock,
              iconCls: 'text-gray-400',
            },
            {
              label: t('feedback.processing'),
              value: stats.processing,
              Icon: CircleAlert,
              iconCls: 'text-blue-400',
            },
            {
              label: t('feedback.resolved'),
              value: stats.resolved,
              Icon: CircleCheckBig,
              iconCls: 'text-green-400',
            },
            {
              label: t('feedback.total'),
              value: stats.total,
              Icon: MessageCircle,
              iconCls: 'text-purple-400',
            },
          ].map(({ label, value, Icon, iconCls }) => (
            <div
              key={label}
              className="bg-white rounded-lg border border-border py-6 px-6"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">{label}</div>
                <Icon className={`w-5 h-5 ${iconCls}`} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search + Filters */}
      <div className="bg-white rounded-xl p-4 border border-border">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-5 h-5" />
            </span>
            <Input
              type="text"
              placeholder={t('feedback.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(val) => setStatusFilter(val)}
          >
            <SelectTrigger className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('feedback.all_status')}</SelectItem>
              <SelectItem value="pending">{t('feedback.pending')}</SelectItem>
              <SelectItem value="processing">
                {t('feedback.processing')}
              </SelectItem>
              <SelectItem value="resolved">{t('feedback.resolved')}</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={typeFilter}
            onValueChange={(val) => setTypeFilter(val)}
          >
            <SelectTrigger className="w-auto">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('feedback.all_types')}</SelectItem>
              <SelectItem value="feature_request">
                {t('feedback.type_feature_request')}
              </SelectItem>
              <SelectItem value="bug_report">
                {t('feedback.type_bug_report')}
              </SelectItem>
              <SelectItem value="suggestion">
                {t('feedback.type_suggestion')}
              </SelectItem>
              <SelectItem value="other">{t('feedback.type_other')}</SelectItem>
            </SelectContent>
          </Select>
          {(search || statusFilter !== 'all' || typeFilter !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSearchParams({})}
            >
              {t('common.clear_all_filters')}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card className="gap-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('feedback.feedback_title')}</TableHead>
              <TableHead>{t('feedback.type')}</TableHead>
              <TableHead>{t('feedback.status')}</TableHead>
              <TableHead>{t('feedback.submitted_at')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {feedbacks.map((feedback) => {
              const st =
                STATUS_LABELS[feedback.status] || STATUS_LABELS.pending;
              const tp = TYPE_LABELS[feedback.type] || TYPE_LABELS.other;

              return (
                <TableRow
                  key={feedback.id}
                  onClick={() => handleRowClick(feedback)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <div className="text-sm font-medium text-foreground">
                      {feedback.title || feedback.content.slice(0, 50)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate max-w-[400px]">
                      {feedback.content.slice(0, 80)}
                      {feedback.content.length > 80 ? '...' : ''}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{tp.label}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={st.variant} className="gap-1">
                      <st.icon className="w-3 h-3" />
                      {st.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDateTime(feedback.createdAt)}
                  </TableCell>
                </TableRow>
              );
            })}
            {feedbacks.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="py-12 text-center text-sm text-muted-foreground"
                >
                  {t('feedback.no_feedback')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={PAGE_LIMIT}
          onPageChange={(p) => {
            setPage(p);
            loadData(p);
          }}
        />
      </Card>
    </div>
  );
};

export default FeedbackManagement;
